import { yupResolver } from '@hookform/resolvers/yup';
import get from 'lodash/get';
import { useEffect, useMemo, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import type { Resolver, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { GlobalError } from 'react-hook-form/dist/types/errors';
import { useIntl } from 'react-intl';

import { CountryCodeEnum } from '@/types/regionTypes';

import { CheckboxField } from '@/components/CheckboxField';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { TitleField } from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import { SelectField } from '@/components/SelectField';
import { Typography } from '@/components/Typography';
import { useUser } from '@/hooks/useUser';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { getRecaptchaLanguageCode } from '@/lib/recaptcha';
import { DEFAULT_COUNTRY, getCountryByCountryCode } from '@/lib/regions';
import * as M from '@/messages/catalogDownload';
import { commonMessages } from '@/messages/common';
import {
  AccountCreationConfirmationNote,
  formMessages,
  PersonalDataPolicy,
} from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { CountryOption, LanguageOption } from '@/views/SignUpView/helper';
import {
  getCountrySelectOptions,
  getNameFieldOrder,
  SortedNameFields,
} from '@/views/SignUpView/helper';

import { getDefaultLanguageOptions, resolveLanguageCode } from './helper';
import { PhoneInput } from './phoneInput';
import type { CatalogDownloadSchema } from './schema';
import { catalogDownloadSchema } from './schema';

import * as S from './CatalogDownloadForm.styles';

export interface CatalogDownloadFormProps {
  disabled?: boolean;
  handleSubmit: SubmitHandler<CatalogDownloadSchema>;
  isProcessing?: boolean;
}

export const CatalogDownloadForm = ({
  disabled,
  handleSubmit,
  isProcessing = false,
}: CatalogDownloadFormProps) => {
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const {
    country: routerCountry,
    locale: routerLocale,
    query: { languageCode },
  } = useRegionProvider();

  const { formatMessage } = useIntl();
  const {
    snapshot: { user },
  } = useUser();

  const userPreferredCountryCode = getMetaValue(
    user?.metadata ?? [],
    ACCOUNT_METADATA.country,
  );
  const userPreferredCountry = getCountryByCountryCode(
    userPreferredCountryCode,
  );
  const userPreferredLanguageCode = getMetaValue(
    user?.metadata ?? [],
    ACCOUNT_METADATA.language,
  );

  const defaultCountryCode =
    userPreferredCountry?.code || routerCountry.code || DEFAULT_COUNTRY.code;
  const countryOptions = getCountrySelectOptions();

  const defaultCountry = countryOptions.find(
    (item) => item.value == defaultCountryCode,
  );

  const defaultLanguageSlug = resolveLanguageCode(
    userPreferredCountry,
    userPreferredLanguageCode,
    routerLocale.sfCode,
  );
  const initialLanguageOptions = getDefaultLanguageOptions(
    defaultCountry?.locales,
  );

  const defaultLanguage = initialLanguageOptions.find(
    (item) => item.value === defaultLanguageSlug,
  );

  const schema = catalogDownloadSchema(formatMessage, defaultCountryCode, user);

  const defaultValues = {
    ...schema.getDefault(),
    country: defaultCountry!,
    language: defaultLanguage!,
  };

  const formCtx = useForm<CatalogDownloadSchema>({
    defaultValues: defaultValues,
    resolver: yupResolver(schema) as unknown as Resolver<CatalogDownloadSchema>,
  });

  const { formState } = formCtx;
  const captcha = formCtx.watch('captcha');
  const currentCountry = formCtx.watch('country') as CountryOption;
  const languageOptions = useMemo(
    () => getDefaultLanguageOptions(currentCountry?.locales ?? []),
    [currentCountry],
  ) as LanguageOption[];

  const [isVerifyingRecaptcha, setIsVerifyingRecaptchaStatus] = useState(false);
  const [isFormFieldDisabled, setIsFormFieldDisabled] = useState(false);

  const phoneError = get(formState.errors, 'phone') as GlobalError | undefined;
  const error = phoneError
    ? null
    : (get(formState.errors, 'billingAddress.phone') as
        | GlobalError
        | undefined);

  useEffect(() => {
    if (user?.id) {
      setIsFormFieldDisabled(true);
      formCtx.reset(defaultValues);
    }
  }, [user?.id]);

  const isJapan = defaultCountryCode === CountryCodeEnum.JP;

  const userTitle = getMetaValue(
    user?.metadata ?? [],
    ACCOUNT_METADATA.personalTitle,
  );
  const userFirstNameKanji = getMetaValue(
    user?.metadata ?? [],
    ACCOUNT_METADATA.first_name_kanji,
  );
  const userLastNameKanji = getMetaValue(
    user?.metadata ?? [],
    ACCOUNT_METADATA.last_name_kanji,
  );
  const personalTitle = isJapan && userTitle === 'Mrs' ? 'Ms' : userTitle;

  const recaptchaLanguageCode = getRecaptchaLanguageCode(languageCode);

  return (
    <FormProvider
      name='catalog-download-form'
      {...formCtx}
      onSubmit={(data) => {
        handleSubmit({
          ...data,
          recaptchaResponse: reCaptchaRef.current?.getValue() ?? '',
        });
      }}
    >
      <S.Row gapDensity='none'>
        <Col data-testid='CatalogDownloadFormTitleRadios' md={6}>
          <TitleField
            countryCode={defaultCountryCode}
            isDisabled={isFormFieldDisabled && !!personalTitle}
            name='salutation'
          />
        </Col>
      </S.Row>
      <S.Row gapDensity='none'>
        <SortedNameFields
          arr={[
            {
              component: (
                <Col key='firstName' md={6}>
                  <InputField
                    autoComplete='given-name'
                    className={CONTENTSQUARE_MASKED}
                    colorVariant='grey'
                    data-testid='CatalogDownloadFormFirstName'
                    disabled={isFormFieldDisabled}
                    name='firstName'
                    placeholder={formatMessage(
                      isJapan
                        ? formMessages.firstNameForJP
                        : formMessages.firstName,
                    )}
                    required
                    style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
                  />
                </Col>
              ),
              order: getNameFieldOrder(
                defaultCountryCode,
                defaultLanguageSlug,
                'first',
              ),
            },
            {
              component: (
                <Col key='lastName' md={6}>
                  <InputField
                    autoComplete='additional-name'
                    className={CONTENTSQUARE_MASKED}
                    colorVariant='grey'
                    data-testid='CatalogDownloadFormLastName'
                    disabled={isFormFieldDisabled}
                    name='lastName'
                    placeholder={formatMessage(
                      isJapan
                        ? formMessages.lastNameForJP
                        : formMessages.lastName,
                    )}
                    required
                    style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
                  />
                </Col>
              ),
              order: getNameFieldOrder(
                defaultCountryCode,
                defaultLanguageSlug,
                'last',
              ),
            },
            ...(isJapan
              ? [
                  {
                    component: (
                      <Col key='firstNameKanji' md={6}>
                        <InputField
                          autoComplete='additional-name'
                          className={CONTENTSQUARE_MASKED}
                          colorVariant='grey'
                          data-testid='CatalogDownloadFormLastName'
                          disabled={isFormFieldDisabled && !!userFirstNameKanji}
                          name='firstNameKanji'
                          placeholder={formatMessage(
                            formMessages.firstNameKanji,
                          )}
                          required
                          style={{
                            borderBottom: '1px solid hsla(0,0%,39%,.3)',
                          }}
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(
                      defaultCountryCode,
                      defaultLanguageSlug,
                      'firstKanji',
                    ),
                  },
                  {
                    component: (
                      <Col key='lastNameKanji' md={6}>
                        <InputField
                          autoComplete='additional-name'
                          className={CONTENTSQUARE_MASKED}
                          colorVariant='grey'
                          data-testid='CatalogDownloadFormlastNameKanji'
                          disabled={isFormFieldDisabled && !!userLastNameKanji}
                          name='lastNameKanji'
                          placeholder={formatMessage(
                            formMessages.lastNameKanji,
                          )}
                          required
                          style={{
                            borderBottom: '1px solid hsla(0,0%,39%,.3)',
                          }}
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(
                      defaultCountryCode,
                      defaultLanguageSlug,
                      'lastKanji',
                    ),
                  },
                ]
              : []),
          ]}
        />
      </S.Row>
      <S.Row gapDensity='none'>
        <Col md={6}>
          <InputField
            autoComplete='email'
            className={CONTENTSQUARE_MASKED}
            colorVariant='grey'
            data-testid='CatalogDownloadFormEmail'
            disabled={isFormFieldDisabled}
            id='inputEmail'
            name='email'
            placeholder={formatMessage(formMessages.email)}
            required
            style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
            type='email'
          />
        </Col>
        <Col md={6}>
          <PhoneInput error={error} />
        </Col>
      </S.Row>
      <S.Row gapDensity='none'>
        <Col md={6}>
          <SelectField
            colorVariant='grey'
            data-testid='CatalogDownloadFormLanguage'
            isDisabled={isFormFieldDisabled}
            name='language'
            options={languageOptions}
            placeholder={formatMessage(formMessages.language)}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderBottom: '1px solid rgba(99, 99, 99, 0.3)',
              }),
            }}
          />
        </Col>
        <Col md={6}>
          <SelectField
            colorVariant='grey'
            isDisabled={true}
            name='country'
            options={countryOptions}
            placeholder={formatMessage(formMessages.country)}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderBottom: '1px solid rgba(99, 99, 99, 0.3)',
              }),
            }}
          />
        </Col>
        <Col md={12}>
          <Typography color='secondaryGrey' scale='p3' textAlign='right'>
            {formatMessage(formMessages.mandatoryFields)}
          </Typography>
        </Col>
        <Col data-testid='CatalogDownloadFormAccountRegisterCheckbox'>
          <CheckboxField
            data-testid='checkboxCatalogDownloadConsent'
            disabled={disabled}
            name='catalogDownloadConsent'
          >
            <Flex flexDirection='column' gridGap='s075'>
              <Typography data-testid='AccountRegisterCheckbox-Text-CatalogDownload'>
                {M.CatalogDownloadKeepInformed}
              </Typography>
              <Typography data-testid='AccountRegisterCheckbox-Text-RevokeConsent'>
                {PersonalDataPolicy}
              </Typography>
            </Flex>
          </CheckboxField>
        </Col>
        <Col data-testid='CatalogDownloadPrivacyConfirm'>
          <CheckboxField
            data-testid='catalogDownloadPrivacyConfirm'
            disabled={disabled}
            name='catalogDownloadPrivacyConfirm'
          >
            <Flex flexDirection='column' gridGap='s075'>
              <Typography data-testid='CatalogDownloadPrivacyConfirm'>
                {AccountCreationConfirmationNote(
                  formatMessage(commonMessages.submit),
                  formatMessage(commonMessages.breitlingTermsOfUse),
                )}
              </Typography>
            </Flex>
          </CheckboxField>
        </Col>
        <Col>
          <ReCaptcha
            ref={reCaptchaRef}
            data-testid='checkboxCaptcha'
            languageCode={recaptchaLanguageCode}
            name='captcha'
            onChange={setIsVerifyingRecaptchaStatus}
          />
        </Col>
        <Col>
          <Flex justifyContent='center'>
            <LegacyButton
              data-testid='CatalogDownloadFormButtonSubmit'
              disabled={
                isProcessing || !captcha || isVerifyingRecaptcha || disabled
              }
              isProcessing={isProcessing || isVerifyingRecaptcha}
              label={formatMessage(commonMessages.submit)}
              type='submit'
            />
          </Flex>
        </Col>
      </S.Row>
    </FormProvider>
  );
};
